import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useInspections } from '@/hooks/useInspections';
import { useProjectNotes } from '@/hooks/useProjectNotes';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Plus, Calendar, FileText, ExternalLink, Image as ImageIcon, StickyNote } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Project } from '@/types/crm';

export default function InspectorProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { inspections, isLoading: inspectionsLoading } = useInspections(projectId);
  const { notes, loading: notesLoading } = useProjectNotes(projectId);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project-detail', projectId],
    queryFn: async () => {
      console.debug('Fetching inspector project', { projectId });
      
      // Step 1: Fetch base project first (RLS-proof)
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId!)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) throw error;
      console.debug('Inspector project fetch result', { projectId, found: !!data });
      
      if (!data) return null;

      // Step 2: Enrich with organization data (if accessible via RLS)
      if (data.org_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', data.org_id)
          .is('deleted_at', null)
          .maybeSingle();
        
        if (orgData) {
          (data as any).organization = orgData;
        }
      }

      return data as unknown as Project | null;
    },
    enabled: !!projectId,
  });

  const projectInspections = statusFilter === 'all'
    ? inspections
    : inspections?.filter(i => i.status === statusFilter);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      concept: 'bg-gray-100 text-gray-800',
      ingediend: 'bg-blue-100 text-blue-800',
      verwerkt: 'bg-green-100 text-green-800',
    };
    return variants[status] || variants.concept;
  };

  // Get project image
  const projectImage = project?.project_images && Array.isArray(project.project_images) && project.project_images.length > 0
    ? (typeof project.project_images[0] === 'string' ? project.project_images[0] : project.project_images[0]?.url)
    : null;

  // Get SharePoint links
  const sharepointLinks = project?.sharepoint_links && Array.isArray(project.sharepoint_links)
    ? project.sharepoint_links
    : [];

  if (projectLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-12 text-center">
          <h3 className="text-lg font-semibold mb-2">Project niet gevonden</h3>
          <p className="text-muted-foreground mb-4">Dit project bestaat niet of je hebt geen toegang.</p>
          <Button onClick={() => navigate('/inspector')}>Terug naar dashboard</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/inspector')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">{project.name}</h1>
              <p className="text-muted-foreground mt-1">
                {project.project_number && `Project #${project.project_number} • `}
                {project.organization?.name || 'Geen organisatie'}
              </p>
            </div>
            <Button onClick={() => navigate(`/inspector/upload?project=${projectId}`)}>
              <Plus className="h-4 w-4 mr-2" />
              Nieuwe Inspectie
            </Button>
          </div>
        </div>
      </div>

      {/* Project Image */}
      {projectImage && (
        <Card className="overflow-hidden">
          <img
            src={projectImage}
            alt={project.name}
            className="w-full h-64 object-cover"
          />
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="inspections" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inspections">Inspecties</TabsTrigger>
          <TabsTrigger value="sharepoint">SharePoint</TabsTrigger>
          <TabsTrigger value="notes">Notities</TabsTrigger>
        </TabsList>

        {/* Inspections Tab */}
        <TabsContent value="inspections" className="space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              Alle
            </Button>
            <Button
              variant={statusFilter === 'concept' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('concept')}
            >
              Concept
            </Button>
            <Button
              variant={statusFilter === 'ingediend' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('ingediend')}
            >
              Ingediend
            </Button>
            <Button
              variant={statusFilter === 'verwerkt' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('verwerkt')}
            >
              Verwerkt
            </Button>
          </div>

          {inspectionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : projectInspections && projectInspections.length > 0 ? (
            <div className="space-y-3">
              {projectInspections.map(inspection => (
                <Card
                  key={inspection.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/inspector/inspection/${inspection.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{inspection.type}</h3>
                        <Badge className={getStatusBadge(inspection.status)}>
                          {inspection.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{format(new Date(inspection.date), 'PPP', { locale: nl })}</span>
                        </div>
                        {inspection.photo_urls && Array.isArray(inspection.photo_urls) && (
                          <div className="flex items-center gap-1">
                            <ImageIcon className="h-4 w-4" />
                            <span>{inspection.photo_urls.length} foto's</span>
                          </div>
                        )}
                      </div>
                      {inspection.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {inspection.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Geen inspecties</h3>
              <p className="text-muted-foreground mb-4">
                Er zijn nog geen inspecties voor dit project
              </p>
              <Button onClick={() => navigate(`/inspector/upload?project=${projectId}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Eerste inspectie toevoegen
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* SharePoint Tab */}
        <TabsContent value="sharepoint" className="space-y-4">
          {sharepointLinks.length > 0 ? (
            <div className="space-y-3">
              {sharepointLinks.map((link: any, index: number) => (
                <Card key={index} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{link.title || `SharePoint Link ${index + 1}`}</h3>
                      <p className="text-sm text-muted-foreground">{link.url}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(link.url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Openen
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <ExternalLink className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Geen SharePoint links</h3>
              <p className="text-muted-foreground">
                Er zijn geen SharePoint links beschikbaar voor dit project
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          {notesLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          ) : notes && notes.length > 0 ? (
            <div className="space-y-3">
              {notes.map(note => (
                <Card key={note.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold">{note.title}</h3>
                    <Badge variant="outline">{note.note_type}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {note.body}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {format(new Date(note.created_at), 'PPP', { locale: nl })}
                  </p>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <StickyNote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Geen notities</h3>
              <p className="text-muted-foreground">
                Er zijn nog geen notities voor dit project
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
